import React, { useCallback } from 'react'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import styled from '@emotion/styled'

import Box from './Box'
import GrooverText from './GrooverText'
import { Language } from '../../../pure-js/types/Antiloop'
import { PrivateLessonsReceiptMock } from '../mocks/privateLessonsPortalMock'
import { getGrooverText } from '../libs/GrooverTextRepository'

type Props = {
  receipt: PrivateLessonsReceiptMock
  language: Language
}

const RECEIPT_ELEMENT_ID = 'private-lessons-receipt'

const formatPrice = (amountInCents: number, currency: string) => `${Math.round(amountInCents / 100)} ${currency}`

const PrivateLessonsReceipt: React.FC<Props> = ({ receipt, language }) => {
  const locale = language === 'sv' ? 'sv-SE' : language === 'no' ? 'nb-NO' : 'en-US'

  const formatTextWithPlaceholders = (text: string, params: Array<string | number>) => {
    let formatted = text
    params.forEach((param, i) => {
      formatted = formatted.replaceAll(`{${i}}`, param.toString())
    })
    return formatted
  }

  const vatMultiplier = 1 + receipt.vatPercent / 100
  const netAmountInCents = Math.round(receipt.totalPriceInCents / vatMultiplier)
  const vatAmountInCents = receipt.totalPriceInCents - netAmountInCents

  const handleDownloadPDF = useCallback(async () => {
    const element = document.getElementById(RECEIPT_ELEMENT_ID)
    if (!element) return

    const canvas = await html2canvas(element, {
      scale: 2,
      logging: false,
      useCORS: true,
      allowTaint: true
    })
    const imgData = canvas.toDataURL('image/png')

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    })

    const imgWidth = 190
    const pageHeight = 277
    const imgHeight = (canvas.height * imgWidth) / canvas.width

    let heightLeft = imgHeight
    let position = 10

    pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight)
    heightLeft -= pageHeight

    while (heightLeft > 0) {
      position = heightLeft - imgHeight + 10
      pdf.addPage()
      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight)
      heightLeft -= pageHeight
    }

    pdf.save(`${receipt.clientName}_Private_Lessons_Receipt.pdf`)
  }, [receipt.clientName])

  return (
    <ReceiptWrapper fullWidth gap="16px">
      <ReceiptTopRow direction="row" justify="space-between" align="center" fullWidth>
        <ReceiptTitle>
          <GrooverText textKey="privateLessonsPortalReceipt" language={language} />
        </ReceiptTitle>
        <DownloadButton onClick={handleDownloadPDF}>
          <GrooverText textKey="privateLessonsPortalDownloadReceipt" language={language} style={{ color: '#fff' }} />
        </DownloadButton>
      </ReceiptTopRow>

      <ReceiptCard id={RECEIPT_ELEMENT_ID}>
        <HeaderRow direction="row" justify="space-between" align="center" fullWidth>
          <ClientName>{receipt.clientName}</ClientName>
          <Title>
            <GrooverText textKey="privateLessonsPortalReceipt" language={language} />
          </Title>
        </HeaderRow>

        <Grid>
          <Label>
            <GrooverText textKey="privateLessonsPortalReceiptOrderId" language={language} />
          </Label>
          <Value>{receipt.receiptId}</Value>

          <Label>
            <GrooverText textKey="privateLessonsPortalReceiptDate" language={language} />
          </Label>
          <Value>{new Date(receipt.createdAtIso).toLocaleString(locale)}</Value>

          <Label>
            <GrooverText textKey="privateLessonsPortalReceiptCustomer" language={language} />
          </Label>
          <Value>
            {receipt.userName} ({receipt.userEmail})
          </Value>

          <Label>
            <GrooverText textKey="privateLessonsPortalReceiptPackage" language={language} />
          </Label>
          <Value>
            {receipt.packageName} • {receipt.lessonCount} × {receipt.durationMin} min
          </Value>

          <Label>
            <GrooverText textKey="privateLessonsPortalReceiptPaymentMethod" language={language} />
          </Label>
          <Value>{receipt.paymentMethod}</Value>

          <Label>
            <GrooverText textKey="privateLessonsPortalReceiptNetAmount" language={language} />
          </Label>
          <Value>{formatPrice(netAmountInCents, receipt.currency)}</Value>

          <Label>
            {formatTextWithPlaceholders(getGrooverText('privateLessonsPortalReceiptVatRate', language), [
              receipt.vatPercent
            ])}
          </Label>
          <Value>{formatPrice(vatAmountInCents, receipt.currency)}</Value>
        </Grid>

        <Divider />

        <TotalRow direction="row" justify="space-between" align="center" fullWidth>
          <TotalLabel>
            <GrooverText textKey="privateLessonsPortalReceiptTotal" language={language} />
          </TotalLabel>
          <TotalValue>{formatPrice(receipt.totalPriceInCents, receipt.currency)}</TotalValue>
        </TotalRow>
      </ReceiptCard>
    </ReceiptWrapper>
  )
}

const ReceiptWrapper = styled(Box)``

const ReceiptCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px 24px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`

const ReceiptTopRow = styled(Box)`
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`

const ReceiptTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #000;
  margin: 0;
`

const HeaderRow = styled(Box)`
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
`

const ClientName = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: #000;
`

const Title = styled.div`
  color: #000;
  font-weight: 600;
  font-size: 15px;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 12px 16px;

  @media (max-width: 768px) {
    grid-template-columns: 120px 1fr;
    gap: 10px 12px;
  }
`

const Label = styled.div`
  color: #666;
  font-size: 14px;
`

const Value = styled.div`
  color: #000;
  font-size: 14px;
  word-break: break-word;
`

const Divider = styled.div`
  height: 1px;
  background: #e5e7eb;
  margin: 16px 0;
`

const TotalRow = styled(Box)``

const TotalLabel = styled.div`
  color: #000;
  font-weight: 500;
  font-size: 14px;
`

const TotalValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #7c3aed;
`

const DownloadButton = styled.button`
  padding: 12px 20px;
  background: #7c3aed;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
  transition: background 0.2s;

  &:hover {
    background: #6d28d9;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`

export default PrivateLessonsReceipt
